// Product-usage analytics client (the "data flywheel").
//
// Fire-and-forget: track() buffers an event, a short timer (or page-hide) flushes
// the buffer to the record_app_events RPC. We POST directly to the PostgREST RPC
// endpoint rather than through supabase-js so this also works on the public/demo
// host, which deliberately never loads the studio Supabase client.
//
// Default-on: behavioral + plan-design analytics send whenever env is configured
// and the user has not opted out (localStorage flag). Census values and direct
// identifiers never enter this module — props are scrubbed by sanitizeProps and
// re-stripped server-side.
import { getDeviceId, getSessionId } from '@/lib/analytics/identity'
import { APP_VERSION, getPage, getSurface, getUaContext } from '@/lib/analytics/context'
import { sanitizeProps } from '@/lib/analytics/redactProps'

const SUPABASE_URL = (import.meta.env?.VITE_SUPABASE_URL || '').trim()
const PUBLISHABLE_KEY = (import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY || '').trim()

const RPC_URL = SUPABASE_URL ? `${SUPABASE_URL}/rest/v1/rpc/record_app_events` : ''
const OPT_OUT_KEY = 'retira.analytics.optout'
const ONCE_PREFIX = 'retira.analytics.once.'
const FLUSH_DELAY_MS = 4000
const MAX_BUFFER = 50

let buffer = []
let flushTimer = null
let started = false
// Studio wires this to return the current Supabase access token so signed-in
// events are stamped with auth.uid() server-side. Anonymous by default.
let authTokenGetter = () => null

export function setAuthTokenGetter(fn) {
  if (typeof fn === 'function') authTokenGetter = fn
}

function isConfigured() {
  return Boolean(RPC_URL && PUBLISHABLE_KEY)
}

export function isOptedOut() {
  if (typeof window === 'undefined') return true
  try {
    return window.localStorage.getItem(OPT_OUT_KEY) === '1'
  } catch {
    return false
  }
}

export function setOptedOut(value) {
  if (typeof window === 'undefined') return
  try {
    if (value) {
      window.localStorage.setItem(OPT_OUT_KEY, '1')
      buffer = []
    } else {
      window.localStorage.removeItem(OPT_OUT_KEY)
    }
  } catch {
    // ignore
  }
}

function canSend() {
  return isConfigured() && !isOptedOut()
}

async function postBatch(events, { keepalive = false } = {}) {
  if (!events.length || !isConfigured()) return
  const ua = getUaContext()
  const token = (() => {
    try {
      return authTokenGetter() || null
    } catch {
      return null
    }
  })()

  const body = JSON.stringify({
    events,
    input_device_id: getDeviceId(),
    input_session_id: getSessionId(),
    input_surface: getSurface(),
    input_app_version: APP_VERSION,
    input_ua_browser: ua.browser,
    input_ua_os: ua.os,
    input_viewport_bucket: ua.viewport,
  })

  try {
    await fetch(RPC_URL, {
      method: 'POST',
      keepalive,
      headers: {
        'Content-Type': 'application/json',
        apikey: PUBLISHABLE_KEY,
        Authorization: `Bearer ${token || PUBLISHABLE_KEY}`,
      },
      body,
    })
  } catch {
    // Analytics is best-effort. Never surface a failure to the UI.
  }
}

export function flush(options = {}) {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  if (!buffer.length) return
  const events = buffer
  buffer = []
  void postBatch(events, options)
}

function scheduleFlush() {
  if (flushTimer || typeof window === 'undefined') return
  flushTimer = setTimeout(() => {
    flushTimer = null
    flush()
  }, FLUSH_DELAY_MS)
}

export function track(name, props = {}) {
  if (!name || typeof name !== 'string') return
  if (!canSend()) return
  buffer.push({
    name,
    page: getPage(),
    props: sanitizeProps(props, { allowDeidentifiedQuestion: name === 'ai.assistant.turn' }),
  })
  if (buffer.length >= MAX_BUFFER) flush()
  else scheduleFlush()
}

// Emits at most once per browser for a given key (e.g. "first_export").
export function trackOnce(key, name, props = {}) {
  if (!key || typeof window === 'undefined') return
  const storageKey = `${ONCE_PREFIX}${key}`
  try {
    if (window.localStorage.getItem(storageKey) === '1') return
    window.localStorage.setItem(storageKey, '1')
  } catch {
    // if storage is unavailable, fall through and just track
  }
  track(name, props)
}

// Call once at app start. Wires page-hide flushing so buffered events survive a
// tab close / navigation.
export function initAnalytics() {
  if (started || typeof window === 'undefined') return
  started = true
  const flushOnHide = () => {
    if (document.visibilityState === 'hidden') flush({ keepalive: true })
  }
  document.addEventListener('visibilitychange', flushOnHide)
  window.addEventListener('pagehide', () => flush({ keepalive: true }))
}
